import { Card, CardContent, CardHeader } from "@volca/ui/components/ui/card";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { isNumericType } from "@/profitability/missing";
import {
  ALL_SECTIONS,
  type ReviewSection,
} from "@/profitability/review/review-spec";
import { PERIOD_DETAILS_COLUMNS } from "./overview-details";
import type { ReportOverview } from "./types";

/** The performance panel beside its reading and review handoff. */
export const DASHBOARD_COLUMNS =
  "@5xl:grid-cols-[minmax(0,1fr)_minmax(18rem,21rem)]";

/** The headline figures, in the strip they land in. */
export const FiguresSkeleton = ({ count = 4 }: { count?: number }) => (
  <div
    className="grid w-full min-w-0 grid-cols-2 gap-x-8 gap-y-4 @3xl:grid-cols-4"
    aria-hidden
  >
    {Array.from({ length: count }, (_, figure) => (
      <Stack key={figure} gap={2}>
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-8 w-24" />
      </Stack>
    ))}
  </div>
);

/** The aside blocks this period will actually draw. */
const shownSections = (overview: ReportOverview): ReviewSection[] =>
  (overview.review?.sections ?? ALL_SECTIONS).filter((section) => {
    switch (section) {
      case "week":
      case "progress":
        return !!overview.review?.work;
      case "insights":
        return overview.insights.length > 0;
      case "changes":
        return !!overview.previous?.comparable;
      case "timeline":
        return true;
      default: {
        const exhaustive: never = section;
        return exhaustive;
      }
    }
  });

/**
 * What the dashboard will draw, taken from a period already in hand. The
 * figures, the review list, and the aside are whatever this report declares,
 * not a fixed page.
 */
export type DashboardShape = {
  figures: number;
  performance: boolean;
  review: boolean;
  sections: ReviewSection[];
  /**
   * Open findings in the review list. Absent when this report does not
   * review records.
   */
  queue?: number;
};

export const dashboardShape = (
  overview: ReportOverview,
  requestedId: string | null,
): DashboardShape => {
  const listed = overview.periods?.find(
    (period) => period.run_id === (requestedId ?? overview.run?.id),
  );
  return {
    figures: overview.figures.length,
    performance: isNumericType(overview.figures[0]?.type ?? ""),
    review: !!overview.review?.work,
    sections: shownSections(overview),
    queue: overview.review?.work ? (listed?.active_findings ?? 4) : undefined,
  };
};

/** Holds the same script-dependent content regions as the loaded briefing. */
export const OverviewSkeleton = ({ shape }: { shape?: DashboardShape }) => (
  <Stack gap={8} className="@container w-full flex-1" aria-busy role="status">
    <span className="sr-only">Loading the period</span>
    <Stack
      className={cn("grid items-start gap-6", {
        [DASHBOARD_COLUMNS]:
          !shape ||
          (shape.figures > 0 &&
            (shape.review || shape.sections.includes("insights"))),
      })}
      aria-hidden
    >
      {(!shape || shape.figures > 0) && (
        <Card className="@container/performance min-w-0">
          <CardHeader>
            <Skeleton className="h-5 w-28" />
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            <Skeleton className="h-9 w-full" />
            <Stack gap={2}>
              <Skeleton className="h-12 w-56 max-w-full" />
              <Skeleton className="h-4 w-32" />
            </Stack>
            {(!shape || shape.figures > 1) && (
              <Stack className="grid grid-cols-2 gap-5 @sm/performance:grid-cols-3">
                {Array.from(
                  { length: Math.max((shape?.figures ?? 4) - 1, 0) },
                  (_, index) => (
                    <Skeleton key={index} className="h-16 w-full" />
                  ),
                )}
              </Stack>
            )}
          </CardContent>
          {(!shape || shape.performance) && (
            <CardContent className="flex flex-col gap-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-64 w-full" />
            </CardContent>
          )}
        </Card>
      )}
      {(!shape || shape.review || shape.sections.includes("insights")) && (
        <Stack gap={6} className="min-w-0">
          {(!shape || shape.sections.includes("insights")) && (
            <Card size="sm">
              <CardContent className="flex flex-col gap-4">
                <Skeleton className="h-6 w-32" />
                <Stack gap={3}>
                  {[0, 1, 2].map((index) => (
                    <Skeleton key={index} className="h-16 w-full" />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
          {(!shape || shape.review) && (
            <Card>
              <CardHeader className="gap-2">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-5 w-32" />
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <Skeleton className="h-8 w-40" />
                <Stack gap={0}>
                  {Array.from(
                    { length: Math.min(shape?.queue ?? 3, 3) },
                    (_, row) => (
                      <Skeleton key={row} className="h-11 w-full" />
                    ),
                  )}
                </Stack>
                <Skeleton className="h-8 w-full" />
              </CardContent>
            </Card>
          )}
        </Stack>
      )}
    </Stack>
    {(!shape || shape.sections.some((section) => section !== "insights")) && (
      <Stack gap={6} className="border-t border-line pt-6" aria-hidden>
        <Skeleton className="h-5 w-32" />
        <Stack
          className={cn("grid items-start gap-6", {
            [PERIOD_DETAILS_COLUMNS]:
              !shape ||
              (shape.sections.includes("week") &&
                (shape.sections.includes("progress") ||
                  shape.sections.includes("timeline"))),
          })}
        >
          {(!shape || shape.sections.includes("week")) && (
            <Skeleton className="h-52 w-full rounded-xl" />
          )}
          {(!shape ||
            shape.sections.includes("progress") ||
            shape.sections.includes("timeline")) && (
            <Skeleton className="h-52 w-full rounded-xl" />
          )}
        </Stack>
        {(!shape || shape.sections.includes("changes")) && (
          <Skeleton className="h-44 w-full rounded-xl" />
        )}
      </Stack>
    )}
  </Stack>
);
